import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { ParentIssueType } from '@risksmart-app/domain/src/types/consts';
import type { GetIssuesByParentIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetIssuesByParentIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

const mapTrpcIssuesToGraphQL = (
  trpcData: GetIssuesByParentIdResponseRow[]
): GetIssuesByParentIdQuery => {
  return {
    issue: trpcData,
  };
};

export const useGetIssuesByParentId = (
  parentId: string,
  type: ParentIssueType,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.issue.issuesByParentId.queryOptions({ parentId, type }),
    gcTime: 60,
    staleTime: 0,
    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData ? mapTrpcIssuesToGraphQL(trpcData) : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
